import { ReactNode, useEffect, useCallback, useMemo, useState } from 'react';
import { AuthContext } from './AuthContext';
import { useAuthState } from '@/hooks/auth/useAuthState';
import { useSignIn } from '@/hooks/auth/useSignIn';
import { useSignUp } from '@/hooks/auth/useSignUp';
import { useSignOut } from '@/hooks/auth/useSignOut';
import { useResetPassword } from '@/hooks/auth/useResetPassword';
import { useSessionManagement } from '@/hooks/auth/useSessionManagement';
import { analytics } from '@/services/analyticsService';
import { AuthResult, SignInCredentials, AuthError } from './types';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 * Provides auth state and methods to the app
 * 
 * Note: Subscription management has been removed.
 * Credits are now managed via the useCredits hook.
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Get auth state and methods from custom hooks
  const { 
    session, 
    user, 
    profile, 
    isLoading, 
    isInitialized, 
    isAuthenticated, 
    isAdmin,
    error,
    lastActivity,
    sessionExpiration,
    fetchProfile,
    updateLastActivity,
  } = useAuthState();

  const [viewAsRole, setViewAsRole] = useState<'admin' | 'beta' | 'regular'>('admin');
  const viewAsUser = viewAsRole !== 'admin';
  const effectiveIsAdmin = isAdmin && !viewAsUser;
  // Cycle: admin → beta → regular → admin
  const toggleViewAsUser = useCallback(() => {
    setViewAsRole(current => {
      if (current === 'admin') return 'beta';
      if (current === 'beta') return 'regular';
      return 'admin';
    });
  }, []);

  const { signIn: signInHook, isLoading: isSigningIn } = useSignIn();
  const { signUp, isLoading: isSigningUp } = useSignUp();
  const { signOut: signOutHook, isLoading: isSigningOut } = useSignOut();
  const { requestPasswordReset, resetPassword: updatePassword } = useResetPassword();
  const { refreshSession } = useSessionManagement();
  
  // Reset "view as" mode on sign-out
  useEffect(() => {
    if (!isAuthenticated) {
      setViewAsRole('admin');
    }
  }, [isAuthenticated]);

  // Update last activity on auth changes (simplified)
  useEffect(() => {
    if (isAuthenticated) {
      updateLastActivity();
    }
  }, [isAuthenticated, updateLastActivity]);
  
  // Track user in analytics when authenticated (consolidated here to prevent duplicates)
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      analytics.setUserId(user.id);
    } else {
      // Clear user ID when not authenticated
      analytics.setUserId(null);
    }
  }, [isAuthenticated, user?.id]);
  
  // Adapter for signIn to match the expected interface
  const signIn = useCallback(async (credentials: SignInCredentials): Promise<AuthResult> => {
    try {
      const result = await signInHook(credentials.email, credentials.password);
      if (result.error) {
        return { error: { message: result.error.message } };
      }
      return { user: result.data?.user || undefined };
    } catch (error: any) {
      return { error: { message: error.message } };
    }
  }, [signInHook]);
  
  // Adapter for signOut to match the expected interface
  const signOut = useCallback(async (): Promise<void> => {
    await signOutHook();
  }, [signOutHook]);

  // Adapter for resetPassword to match the expected interface
  const resetPassword = useCallback(async (email: string): Promise<{ error: AuthError | null }> => {
    return await requestPasswordReset(email);
  }, [requestPasswordReset]);
  
  // Combine auth loading states
  const isAuthLoading = isLoading || isSigningIn || isSigningUp || isSigningOut;
  
  // Memoize authentication context values to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Auth state
    session,
    user,
    profile,
    
    // Status flags
    isLoading: isAuthLoading,
    isInitialized,
    isAuthenticated,
    isAdmin,
    effectiveIsAdmin,
    viewAsRole,
    viewAsUser,
    toggleViewAsUser,
    setViewAsRole,

    // Session info
    lastActivity,
    sessionExpiration,

    // Error state
    error,

    // Authentication methods
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,

    // Session management
    refreshSession,
    updateLastActivity
  }), [
    session,
    user,
    profile,
    isAuthLoading,
    isInitialized,
    isAuthenticated,
    isAdmin,
    effectiveIsAdmin,
    viewAsRole,
    viewAsUser,
    toggleViewAsUser,
    setViewAsRole,
    lastActivity,
    sessionExpiration,
    error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    refreshSession,
    updateLastActivity
  ]);
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
